import React from 'react';
import { ReactComponent as CancerSupportIcon } from 'assets/icons/HealthTopics/CancerSupport.svg';
import { ReactComponent as CardiovascularHealthIcon } from 'assets/icons/HealthTopics/CardiovascularHealth.svg';
import { ReactComponent as CareTransitionsIcon } from 'assets/icons/HealthTopics/CareTransitionsAndHospitalCare.svg';
import { ReactComponent as DiabetesHealthIcon } from 'assets/icons/HealthTopics/DiabetesenDocrineHealth.svg';
import { ReactComponent as KidneyHealthIcon } from 'assets/icons/HealthTopics/KidneyHealth.svg';
import { ReactComponent as MaternalHealthIcon } from 'assets/icons/HealthTopics/MaternalHealth.svg';
import { ReactComponent as MentalHealthIcon } from 'assets/icons/HealthTopics/MentalHealth.svg';
import { ReactComponent as PainManagementIcon } from 'assets/icons/HealthTopics/PainManagement.svg';
import { ReactComponent as PediatricHealthIcon } from 'assets/icons/HealthTopics/PediatricHealth.svg';
import { ReactComponent as RespiratoryHealthIcon } from 'assets/icons/HealthTopics/RespiratoryHealth.svg';
import { ReactComponent as WellbeingIcon } from 'assets/icons/HealthTopics/WellbeingAndSocialSupport.svg';
import { ReactComponent as WomensHealthIcon } from 'assets/icons/HealthTopics/WomensHealth.svg';
import type { HealthTopic } from '../types';
import { HEALTH_TOPIC_COLORS } from '../constants';

/**
 * Color palette keys matching iOS color system
 */
export enum HealthTopicColor {
  PURPLE1 = 'purple1',
  MAGENTA3 = 'magenta3',
  TURQUOISE3 = 'turquoise3',
  SECONDARY2 = 'secondary2',
  GRAY2 = 'gray2',
  TERTIARY2 = 'tertiary2',
  GREEN2 = 'green2',
  LILAC3 = 'lilac3',
  ORANGE3 = 'orange3',
  RED3 = 'red3',
  PURPLE2 = 'purple2',
}

/**
 * Maps API string values to HealthTopic enum
 * Matches iOS: init(from healthTopicString: String)
 */
export const healthTopicFromAPIString = (apiString: string): HealthTopic => {
  const mapping: Record<string, HealthTopic> = {
    CANCER_SUPPORT: 'cancer',
    CARDIOVASCULAR_HEALTH: 'cardiovascular',
    CARE_TRANSITIONS_AND_HOSPITAL_STAY: 'careTransitions',
    DIABETES_AND_PRE_DIABETES_SUPPORT: 'diabetes',
    KIDNEY_HEALTH: 'kidney',
    MATERNAL_HEALTH: 'maternal',
    MENTAL_AND_BEHAVIORAL_HEALTH: 'mentalAndBehavioral',
    PAIN_MANAGEMENT: 'painManagement',
    PEDIATRIC_HEALTH: 'pediatric',
    RESPIRATORY_HEALTH: 'respiratory',
    WELLNESS_AND_SOCIAL_SUPPORT: 'wellbeingAndSocialSupport',
    WOMENS_HEALTH: 'womens',
  };

  return mapping[apiString] || 'generic';
};

/**
 * Maps HealthTopic to color key
 * Matches iOS: var color: Color
 */
export const getHealthTopicColor = (healthTopic: HealthTopic): HealthTopicColor => {
  const colorMapping: Record<HealthTopic, HealthTopicColor> = {
    cancer: HealthTopicColor.PURPLE1,
    cardiovascular: HealthTopicColor.MAGENTA3,
    careTransitions: HealthTopicColor.TURQUOISE3,
    diabetes: HealthTopicColor.SECONDARY2,
    maternal: HealthTopicColor.SECONDARY2,
    generic: HealthTopicColor.GRAY2,
    kidney: HealthTopicColor.TERTIARY2,
    mentalAndBehavioral: HealthTopicColor.GREEN2,
    painManagement: HealthTopicColor.LILAC3,
    womens: HealthTopicColor.LILAC3,
    pediatric: HealthTopicColor.ORANGE3,
    respiratory: HealthTopicColor.RED3,
    wellbeingAndSocialSupport: HealthTopicColor.PURPLE2,
  };

  return colorMapping[healthTopic];
};

/**
 * Display rank for sorting
 * Matches iOS: var rank: Int
 */
export const getHealthTopicRank = (healthTopic: HealthTopic): number => {
  const rankMapping: Record<HealthTopic, number> = {
    wellbeingAndSocialSupport: 1,
    mentalAndBehavioral: 2,
    painManagement: 3,
    cardiovascular: 4,
    maternal: 5,
    diabetes: 6,
    respiratory: 7,
    cancer: 8,
    pediatric: 9,
    womens: 10,
    kidney: 11,
    careTransitions: 12,
    generic: 13,
  };

  return rankMapping[healthTopic];
};

/**
 * Get background color for a health topic
 */
export const getHealthTopicBackgroundColor = (healthTopic: HealthTopic): string => {
  const colorKey = getHealthTopicColor(healthTopic);
  return HEALTH_TOPIC_COLORS[colorKey];
};

/**
 * Get the i18n translation key for a health topic's display name.
 */
export const getHealthTopicTranslationKey = (healthTopic: HealthTopic): string => {
  const keyMapping: Record<HealthTopic, string> = {
    cancer: 'cancer_support',
    cardiovascular: 'cardiovascular_health',
    careTransitions: 'care_transitions_and_hospital_stay',
    diabetes: 'diabetes_and_prediabetes_health',
    kidney: 'kidney_health',
    maternal: 'maternal_health',
    mentalAndBehavioral: 'mental_and_behavioral_health',
    painManagement: 'pain_management',
    pediatric: 'pediatric_health',
    respiratory: 'respiratory_health',
    wellbeingAndSocialSupport: 'wellness_and_social_support',
    womens: 'womens_health',
    generic: 'program_topics',
  };

  return keyMapping[healthTopic];
};

/**
 * Get icon component for a health topic
 */
export const getHealthTopicIcon = (healthTopic: HealthTopic): React.ComponentType => {
  const iconMapping: Record<HealthTopic, React.ComponentType> = {
    cancer: CancerSupportIcon,
    cardiovascular: CardiovascularHealthIcon,
    careTransitions: CareTransitionsIcon,
    diabetes: DiabetesHealthIcon,
    kidney: KidneyHealthIcon,
    maternal: MaternalHealthIcon,
    mentalAndBehavioral: MentalHealthIcon,
    painManagement: PainManagementIcon,
    pediatric: PediatricHealthIcon,
    respiratory: RespiratoryHealthIcon,
    wellbeingAndSocialSupport: WellbeingIcon,
    womens: WomensHealthIcon,
    generic: CancerSupportIcon, // Fallback to a default icon
  };

  return iconMapping[healthTopic];
};

/**
 * Get display title for a health topic
 * Matches the titles shown in the Figma designs
 */
export const getHealthTopicTitle = (healthTopic: HealthTopic): string => {
  const titleMapping: Record<HealthTopic, string> = {
    wellbeingAndSocialSupport: 'Wellness and Social Support',
    mentalAndBehavioral: 'Mental and Behavioral Health',
    painManagement: 'Pain Management',
    cardiovascular: 'Cardiovascular Health',
    maternal: 'Maternal Health',
    diabetes: 'Diabetes and Prediabetes Health',
    respiratory: 'Respiratory Health',
    cancer: 'Cancer Support',
    pediatric: 'Pediatric Health',
    womens: "Women's Health",
    kidney: 'Kidney Health',
    careTransitions: 'Care Transitions and Hospital Stay',
    generic: 'General Health',
  };

  return titleMapping[healthTopic];
};

/**
 * Get all health topics sorted by rank
 * Excludes the 'generic' topic
 */
export const getAllHealthTopicsSorted = (): HealthTopic[] => {
  const topics: HealthTopic[] = [
    'wellbeingAndSocialSupport',
    'mentalAndBehavioral',
    'painManagement',
    'cardiovascular',
    'maternal',
    'diabetes',
    'respiratory',
    'cancer',
    'pediatric',
    'womens',
    'kidney',
    'careTransitions',
  ];

  return topics.sort((a, b) => getHealthTopicRank(a) - getHealthTopicRank(b));
};
